import React, { useEffect, useState, useRef } from 'react';
import { Text } from '../text/index.js';
import { StyledImage, StyledSlide, StyledHeadline, StyledDisclaimer, StyledCustomSlot, StyledTextSlide } from './index.styles.js';
import { throttle } from 'lodash';
import { SYS_SPACE_FIXED_200 } from '@oneaudi/unified-web-alpha-common';
import { TextLink } from '../text-link/index.js';
export const Slide = ({ item, index, aspectRatio, layout, ariaSlideRoleDescription, ariaSlideLabel, totalItems, onVisibilityChange, onWidthChange, isActive = false }) => {
    const [isFullyVisible, setIsFullyVisible] = useState(false);
    const slideRef = useRef(null);
    useEffect(() => {
        const el = slideRef.current;
        if (!el)
            return;
        const getHorizontalVisibility = () => {
            const rect = el.getBoundingClientRect();
            const containerRect = el.parentElement?.getBoundingClientRect();
            if (!containerRect)
                return;
            const fullyVisible = rect.left >= containerRect.left && rect.right <= containerRect.right;
            setIsFullyVisible(fullyVisible);
            onVisibilityChange(index, fullyVisible);
        };
        let resizeObserver;
        if (el.parentElement && 'ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(() => {
                getHorizontalVisibility();
            });
            resizeObserver.observe(el.parentElement);
        }
        const throttledGetHorizontalVisibility = throttle(getHorizontalVisibility, 500);
        window.addEventListener('scroll', throttledGetHorizontalVisibility, true);
        window.addEventListener('resize', throttledGetHorizontalVisibility);
        getHorizontalVisibility();
        return () => {
            if (resizeObserver && el.parentElement) {
                resizeObserver.unobserve(el.parentElement);
                resizeObserver.disconnect();
            }
            window.removeEventListener('scroll', throttledGetHorizontalVisibility, true);
            window.removeEventListener('resize', throttledGetHorizontalVisibility);
        };
    }, [aspectRatio, layout, index]);
    useEffect(() => {
        const el = slideRef.current;
        if (!el)
            return;
        const getSlideWidth = () => {
            const el = slideRef.current;
            if (!el)
                return;
            const rect = el.getBoundingClientRect();
            onWidthChange(rect.width);
        };
        let resizeObserver;
        if (el.parentElement && 'ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(() => {
                getSlideWidth();
            });
            resizeObserver.observe(el.parentElement);
        }
        const throttledGetSlideWidth = throttle(getSlideWidth, 500);
        window.addEventListener('resize', throttledGetSlideWidth);
        getSlideWidth();
        return () => {
            if (resizeObserver && el.parentElement) {
                resizeObserver.unobserve(el.parentElement);
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', throttledGetSlideWidth);
        };
    }, [aspectRatio, layout]);
    const isImage = item.src && item.alt;
    const prevIsActive = useRef(isActive);
    useEffect(() => {
        const el = slideRef.current;
        const parent = el?.parentElement;
        if (!el || !parent)
            return;
        // run only when becoming active (not on every re-render)
        if (isActive && !prevIsActive.current && !isFullyVisible) {
            parent.scrollTo({
                left: el.offsetLeft,
                top: parent.scrollTop,
                behavior: 'smooth'
            });
        }
        prevIsActive.current = isActive;
    }, [isActive, isFullyVisible]);
    return (React.createElement(StyledSlide, { aspectRatio: aspectRatio, layout: layout, "aria-hidden": !isFullyVisible && !isActive, tabIndex: isActive ? 0 : -1, "aria-roledescription": ariaSlideRoleDescription, "aria-label": ariaSlideLabel && ariaSlideLabel(index, totalItems, item.alt), ref: slideRef },
        isImage ? (React.createElement(StyledImage, { src: item.src, alt: item.alt, aspectRatio: aspectRatio })) : (item.videoMarkup && item.videoMarkup()),
        (item.headline ||
            item.copy ||
            item.disclaimer ||
            item.textLink ||
            item.customSlot) && (React.createElement(StyledTextSlide, null,
            item.headline && (React.createElement(StyledHeadline, { tag: "span", variant: {
                    order: '3',
                    weight: 'normal',
                    type: 'headline'
                } }, item.headline.text)),
            item?.copy && React.createElement(Text, null, item.copy),
            item.customSlot && (React.createElement(StyledCustomSlot, null, item.customSlot())),
            item?.textLink && item.textLink.href ? (React.createElement(TextLink, { ...item.textLink, variant: "standalone", size: "md", tabIndex: -1, margin: [SYS_SPACE_FIXED_200, 0, 0, 0] }, item.textLink.children)) : item?.textLink?.onClick ? (React.createElement(TextLink, { ...item.textLink, variant: "standalone", tabIndex: -1, size: "md", margin: [SYS_SPACE_FIXED_200, 0, 0, 0] }, item.textLink.children)) : undefined,
            item.disclaimer && (React.createElement(StyledDisclaimer, null, item.disclaimer))))));
};
//# sourceMappingURL=slide.js.map